import { useRef, useState, useEffect } from "react";
import { Checkbox, Panel, DefaultButton, TextField, Callout} from "@fluentui/react";
import Select, { MultiValue } from 'react-select';
import AristotleLogo from "../../assets/alogo.png";
import AristotleLogoDark from "../../assets/alogo dark.png";

import styles from "./Translate.module.css";

import { chatApi, chatTranslationApi, Approaches, AskResponse, ChatTranslationRequest, ChatTurn } from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { SettingsButton } from "../../components/SettingsButton";
import { ClearChatButton } from "../../components/ClearChatButton";
import { DarkModeSwitch } from "../../components/DarkModeSwitch";
import FileUploadSimple from "../../components/FileUpload/FileUploadSimple";
import { Document, Packer, Paragraph, TextRun } from 'docx';

interface LanguageOption {
    value: string;
    label: string;
}

const Translate = () => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [suppPromptTemplate, setSuppPromptTemplate] = useState<string[]>([]);
    const [displaySuppPromptTemplate, setDisplaySuppPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.8);
    const [useSupplementaryRules, setUseSupplementaryRules] = useState<boolean>(true);
    const [password, setPassword] = useState<string>("");

    const [uploadedText, setUploadedText] = useState(false);
    const [uploadedData, setUploadedData] = useState('');

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: AskResponse][]>([]);

    const languages = [
        { value: 'Albanian', label: 'Albanian (Albania)' },
        { value: 'Amharic', label: 'Amharic (Ethiopia)' },
        { value: 'Arabic', label: 'Arabic (Arab World)' },
        { value: 'Armenian', label: 'Armenian (Armenia)' },
        { value: 'Awadhi', label: 'Awadhi (India)' },
        { value: 'Azerbaijani', label: 'Azerbaijani (Azerbaijan)' },
        { value: 'Bashkir', label: 'Bashkir (Russia)' },
        { value: 'Basque', label: 'Basque (Spain)' },
        { value: 'Belarusian', label: 'Belarusian (Belarus)' },
        { value: 'Bengali', label: 'Bengali (Bangladesh)' },
        { value: 'Bhojpuri', label: 'Bhojpuri (India)' },
        { value: 'Bosnian', label: 'Bosnian (Bosnia and Herzegovina)' },
        { value: 'Brazilian Portuguese', label: 'Brazilian Portuguese (Brazil)' },
        { value: 'Bulgarian', label: 'Bulgarian (Bulgaria)' },
        { value: 'Canadian French', label: 'Canadian French (Canada)' },
        { value: 'Catalan', label: 'Catalan (Spain)' },
        { value: 'Chhattisgarhi', label: 'Chhattisgarhi (India)' },
        { value: 'Chinese Simplified', label: 'Chinese Simplified (China)' },
        { value: 'Chinese Traditional', label: 'Chinese Traditional (China)' },
        { value: 'Croatian', label: 'Croatian (Croatia)' },
        { value: 'Czech', label: 'Czech (Czech Republic)' },
        { value: 'Danish', label: 'Danish (Denmark)' },
        { value: 'Dogri', label: 'Dogri (India)' },
        { value: 'Dutch', label: 'Dutch (Netherlands)' },
        { value: 'English', label: 'English (United Kingdom)' },
        { value: 'Estonian', label: 'Estonian (Estonia)' },
        { value: 'Faroese', label: 'Faroese (Faroe Islands)' },
        { value: 'Finnish', label: 'Finnish (Finland)' },
        { value: 'French', label: 'French (France)' },
        { value: 'Galician', label: 'Galician (Spain)' },
        { value: 'Georgian', label: 'Georgian (Georgia)' },
        { value: 'German', label: 'German (Germany)' },
        { value: 'Greek', label: 'Greek (Greece)' },
        { value: 'Gujarati', label: 'Gujarati (India)' },
        { value: 'Haryanvi', label: 'Haryanvi (India)' },
        { value: 'Hindi', label: 'Hindi (India)' },
        { value: 'Hungarian', label: 'Hungarian (Hungary)' },
        { value: 'Indonesian', label: 'Indonesian (Indonesia)' },
        { value: 'Irish', label: 'Irish (Ireland)' },
        { value: 'Italian', label: 'Italian (Italy)' },
        { value: 'Japanese', label: 'Japanese (Japan)' },
        { value: 'Javanese', label: 'Javanese (Indonesia)' },
        { value: 'Kannada', label: 'Kannada (India)' },
        { value: 'Kashmiri', label: 'Kashmiri (India)' },
        { value: 'Kazakh', label: 'Kazakh (Kazakhstan)' },
        { value: 'Konkani', label: 'Konkani (India)' },
        { value: 'Korean', label: 'Korean (South Korea)' },
        { value: 'Kyrgyz', label: 'Kyrgyz (Kyrgyzstan)' },
        { value: 'Latvian', label: 'Latvian (Latvia)' },
        { value: 'Lithuanian', label: 'Lithuanian (Lithuania)' },
        { value: 'Macedonian', label: 'Macedonian (North Macedonia)' },
        { value: 'Maithili', label: 'Maithili (India)' },
        { value: 'Malay', label: 'Malay (Malaysia)' },
        { value: 'Maltese', label: 'Maltese (Malta)' },
        { value: 'Marathi', label: 'Marathi (India)' },
        { value: 'Marwari', label: 'Marwari (India)' },
        { value: 'Min Nan', label: 'Min Nan (China)' },
        { value: 'Moldovan', label: 'Moldovan (Moldova)' },
        { value: 'Mongolian', label: 'Mongolian (Mongolia)' },
        { value: 'Montenegrin', label: 'Montenegrin (Montenegro)' },
        { value: 'Nepali', label: 'Nepali (Nepal)' },
        { value: 'Norwegian', label: 'Norwegian (Norway)' },
        { value: 'Oriya', label: 'Oriya (India)' },
        { value: 'Pashto', label: 'Pashto (Afghanistan)' },
        { value: 'Persian (Farsi)', label: 'Persian (Farsi) (Iran)' },
        { value: 'Polish', label: 'Polish (Poland)' },
        { value: 'Portuguese', label: 'Portuguese (Portugal)' },
        { value: 'Punjabi', label: 'Punjabi (India)' },
        { value: 'Rajasthani', label: 'Rajasthani (India)' },
        { value: 'Romanian', label: 'Romanian (Romania)' },
        { value: 'Russian', label: 'Russian (Russia)' },
        { value: 'Sanskrit', label: 'Sanskrit (India)' },
        { value: 'Santali', label: 'Santali (India)' },
        { value: 'Serbian', label: 'Serbian (Serbia)' },
        { value: 'Sindhi', label: 'Sindhi (Pakistan)' },
        { value: 'Sinhala', label: 'Sinhala (Sri Lanka)' },
        { value: 'Slovak', label: 'Slovak (Slovakia)' },
        { value: 'Slovene', label: 'Slovene (Slovenia)' },
        { value: 'Slovenian', label: 'Slovenian (Slovenia)' },
        { value: 'Spanish (Latin World)', label: 'Spanish (Latin World)' },
        { value: 'Spanish (Spain)', label: 'Spanish (Spain)' },
        { value: 'Ukrainian', label: 'Ukrainian (Ukraine)' },
        { value: 'Urdu', label: 'Urdu (Pakistan)' },
        { value: 'Uzbek', label: 'Uzbek (Uzbekistan)' },
        { value: 'Vietnamese', label: 'Vietnamese (Vietnam)' },
        { value: 'Welsh', label: 'Welsh (Wales)' },
    ];

    const [selectedLanguages, setSelectedLanguages] = useState<MultiValue<LanguageOption>>([]);

    const handleDropdownChange = (options: MultiValue<LanguageOption>) => {
        setSelectedLanguages(options);
        if (options.length === 0) {
            setSuppPromptTemplate([]);
            setDisplaySuppPromptTemplate("");
        }
    };

    useEffect(() => {
        if (selectedLanguages.length > 0) {
            const selectedLanguageLabels = selectedLanguages.map(lang => lang.label).join(", ");
            console.log(`Selected languages: ${selectedLanguageLabels}`);
            if (useSupplementaryRules) {
                fetchSuppPromptTemplate().then(r => console.log('Retrieved supplementary prompt template'));
            }
        }
    }, [selectedLanguages]);

    const makeTranslationApiRequest = async (question: string, selectedLanguages: MultiValue<LanguageOption>) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        try {
            const history: ChatTurn[] = answers.map(a => ({ user: a[0], bot: a[1].answer }));
            const selectedLanguageValues = selectedLanguages.map(lang => lang.value);
            console.log('Making API request with languages: ', selectedLanguageValues);
            
            const request: ChatTranslationRequest = {
                history: [...history, { user: question, bot: undefined }],
                approach: Approaches.Translate,
                overrides: {
                    promptTemplate: promptTemplate.length === 0 ? undefined : promptTemplate,
                    temperature: temperature,
                    supplementaryRules: useSupplementaryRules,
                    suppPromptTemplate: suppPromptTemplate.length === 0 ? undefined : suppPromptTemplate
                },
                languages: selectedLanguageValues
            };
            const result = await chatTranslationApi(request);
            setAnswers([...answers, [question, result]]);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchSuppPromptTemplate = async () => {
        if (selectedLanguages.length === 0) {
                console.log('No languages selected for supplementary rules');
                setSuppPromptTemplate([]);
                setDisplaySuppPromptTemplate("");
                return;
            }

        try {
            const selectedLanguageValues = selectedLanguages.map(lang => lang.value);
            console.log(selectedLanguageValues);
            const response = await fetch('/api/get-supp-prompt-template', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ languages: selectedLanguageValues }),
            });
            if (!response.ok) {
                console.error('Failed to fetch supplementary prompt template:', response.statusText);
                return;
            }

            const data = await response.json();

            setSuppPromptTemplate(data);
            console.log('SuppPromptTemplate:', suppPromptTemplate);
            setDisplaySuppPromptTemplate(data.join("\n"));


        } catch (error) {
            console.error('Error fetching supplementary prompt template:', error);
        }
    };

    async function validatePassword(password: string) {
        const response = await fetch('/api/validate-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password : password })
          });
        const result = await response.json();
        return result.valid;
    }

    const uploadSuppRules = async () => {
        try {
            const selectedLanguageValues = selectedLanguages.map(lang => lang.value);
            console.log(selectedLanguageValues);

            // No call if no languages are selected
            if (selectedLanguageValues.length === 0) {
                console.error('No languages selected for supplementary rules');
                document.getElementById("uploadRulesStatus")!.innerText = "No languages selected for supplementary rules!";
                return;
            }

            // call to backend to validate password
            if (password === "") {
                document.getElementById("uploadRulesStatus")!.innerText = "Please enter the password to upload!";
                return;
            }

            if (!await validatePassword(password)) {
                document.getElementById("uploadRulesStatus")!.innerText = "Invalid password!";
                return;
            }

            const response = await fetch('/api/update-supp-prompt-template', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ languages: selectedLanguageValues, suppPromptTemplate: suppPromptTemplate }),
            });
            if (!response.ok) {
                console.error('Failed to upload supplementary rules:', response.statusText);
                document.getElementById("uploadRulesStatus")!.innerText = "Failed to upload supplementary rules!";
                return;
            }

            console.log('Uploaded supplementary rules:', response.statusText);
            document.getElementById("uploadRulesStatus")!.innerText = "Uploaded supplementary rules successfully!";
        } catch (error) {
            console.error('Error uploading supplementary rules:', error);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setSelectedLanguages([]);
        setUploadedText(false);
        setUploadedData('');
    };

    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" }), [isLoading]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onSuppPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {

        if (newValue === undefined) {
            setSuppPromptTemplate([]);
        }
        else {
            const lines = newValue.split('\n');
            const parsed = lines.map(item => item.trim());
            console.log("parsed", parsed);
            setSuppPromptTemplate(parsed || []);
        }

        // setSuppPromptTemplate(newValue.split('\n\n') || "");
        setDisplaySuppPromptTemplate(newValue || "");
    };

    const onPasswordChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPassword(newValue || "");
    };

    const onUseSupplementaryRulesChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSupplementaryRules(!!checked);
        if (checked) {
            fetchSuppPromptTemplate().then(r => console.log('Retrieved supplementary prompt template'));
        }
    };

    const onTemperatureChanged = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setTemperature(parseFloat(newValue || "0.5"));
    };

    const onDocTranslateClicked = (doc: string) => {
        setUploadedText(true);
        setUploadedData(doc);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const onDownloadResponse = (response: string, index: number) => {

        // Convert response to word document (docx)
        const lines = response.split('\n');
        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: lines.map(line => new Paragraph({
                        children: [
                            new TextRun(line),
                        ],
                    })),
                },
            ],
        });

        Packer.toBlob(doc).then(blob => {
            const element = document.createElement("a");
            element.href = URL.createObjectURL(blob);
            if (selectedLanguages.length > 1) {
                element.download = "Multi Translation.docx";
            }
            else {
                const selectedLanguage = selectedLanguages.map(lang => lang.value).join(", ");
                element.download = `${selectedLanguage} Translation.docx`;
            }
            document.body.appendChild(element); // Required for this to work in Firefox
            element.click();
            document.body.removeChild(element); // Clean up after download
        });


        setSelectedAnswer(index);

    };

    return (
        <div className={isDarkMode ? styles.containerDark : styles.container}>
            <div className={isDarkMode ? styles.commandsContainerDark : styles.commandsContainer}>
                <ClearChatButton
                    className={isDarkMode ? styles.commandButtonDark : styles.commandButton}
                    onClick={clearChat}
                    disabled={!lastQuestionRef.current || isLoading}
                    isDarkMode={isDarkMode}
                />
                <SettingsButton
                    className={isDarkMode ? styles.commandButtonDark : styles.commandButton}
                    onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)}
                    isDarkMode={isDarkMode}
                />
                <DarkModeSwitch
                    className={isDarkMode ? styles.commandSwitchDark : styles.commandSwitch}
                    onChange={() => setIsDarkMode(!isDarkMode)}
                    isDarkMode={isDarkMode}
                />
            </div>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastQuestionRef.current ? (
                        <div className={styles.chatEmptyState}>
                            <img src={isDarkMode ? AristotleLogoDark : AristotleLogo}></img>
                            {/*<h1 className={styles.chatEmptyStateTitle}>Translate your data</h1>*/}
                            <h2 className={isDarkMode ? styles.chatEmptyStateSubtitleDark : styles.chatEmptyStateSubtitle}>Enter your text or upload a document (.pdf, .docx)</h2>
                            <FileUploadSimple onDocTranslateClicked={onDocTranslateClicked}></FileUploadSimple>
                            <div className={styles.dropdownContainer}>
                            <Select
                                onChange={handleDropdownChange}
                                className={styles['language-dropdown']}
                                options={languages}
                                isSearchable={true}
                                placeholder="Select languages..."
                                isMulti
                            />

                                {/* <select className={styles['language-dropdown']} value={selectedLanguage} onChange={handleDropdownChange}>
                                    {sortedLanguages.map((language) => (
                                        <option key={language.value} value={language.value}>
                                            {language.label}
                                        </option>
                                    ))}
                                </select> */}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {answers.map((answer, index) => (
                                <div key={index}>
                                    <UserChatMessage message={answer[0]} />
                                    <div className={styles.chatMessageGpt}>
                                        <Answer
                                            key={index}
                                            answer={answer[1]}
                                            isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                            onCitationClicked={c => onShowCitation(c, index)}
                                            onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                            onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                            onDownloadResponseClicked={r => onDownloadResponse(r, index)}
                                            onFollowupQuestionClicked={q => makeTranslationApiRequest(q, selectedLanguages)}
                                        />
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={error.toString()} onRetry={() => makeTranslationApiRequest(lastQuestionRef.current, selectedLanguages)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}

                    <div className={isDarkMode ? styles.chatInputDark : styles.chatInput}>
                        <QuestionInput
                            isDarkMode={isDarkMode}
                            clearOnSend
                            placeholder="Enter text to be translated..."
                            disabled={isLoading}
                            onSend={question => makeTranslationApiRequest(question, selectedLanguages)}
                            uploadedText={uploadedText}
                            uploadedData={uploadedData}
                        />
                    </div>
                </div>

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                        isDarkMode={isDarkMode}
                    />
                )}

                <Panel
                    headerText="Configure answer generation"
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel="Close"
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <TextField
                        className={styles.chatSettingsSeparator}
                        defaultValue={promptTemplate}
                        label="Override prompt template"
                        multiline
                        autoAdjustHeight
                        onChange={onPromptTemplateChange}
                    />
                    <TextField
                        className={styles.chatSettingsSeparator}
                        label="Set temperature"
                        defaultValue={temperature.toString()}
                        onChange={onTemperatureChanged}
                    />
                    <Checkbox
                        className={styles.chatSettingsSeparator}
                        checked={useSupplementaryRules}
                        label="Apply additional rules to translation (only certain languages supported)"
                        onChange={onUseSupplementaryRulesChange}
                    />
                    <TextField
                        className={styles.chatSettingsSeparator}
                        label="Override Supplementary Rules"
                        multiline
                        autoAdjustHeight
                        onChange={onSuppPromptTemplateChange}
                        disabled={!useSupplementaryRules}
                        value={displaySuppPromptTemplate}
                    />

                    <DefaultButton
                        className={styles.chatSettingsSeparator}
                        label="Upload supplementary rules"
                        onClick={uploadSuppRules}
                        disabled={!useSupplementaryRules}
                    >
                        Upload Supplementary Rules
                    </DefaultButton>


                    <p
                        className={styles.chatSettingsSeparator}
                        id={"uploadRulesStatus"}
                    ></p>

                    <TextField
                        className={styles.chatSettingsSeparator}
                        type={"password"}
                        label="Enter Password"
                        onChange={onPasswordChange}
                        disabled={!useSupplementaryRules}
                        canRevealPassword
                    />
                </Panel>
            </div>
        </div>
    );
};

export default Translate;
